'use client';

import {Container} from "@/new/components";
import {TitleSection} from "@/new/ui/TitleSection/TitleSection";
import styles from "./CallbackForm.module.scss";
import modalStyles from "@/components/SendCallbackModal/SendCallbackModal.module.scss";
import {ChangeEvent, FormEvent, useEffect, useRef, useState} from "react";
import {toast} from "sonner";
import * as ApiSite from "@/api/site";
import {CheckCircle, X} from "lucide-react";


export const CallbackForm = ({callback}: {callback: string}) => {
  const inputPhone = useRef<HTMLInputElement>(null);
  const inputName = useRef<HTMLInputElement>(null);

  const [phone, setPhone] = useState<string>('');
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [isValidName, setIsValidName] = useState(false);
  const yandexMetrikaId = Number(process.env.NEXT_PUBLIC_YANDEX_METRIKA);

  useEffect(() => {
    if (!isSuccessOpen) return;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSuccessOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSuccessOpen]);

  // Функция для форматирования номера телефона
  const formatPhoneNumber = (value: string): string => {
    if (!value) return '';

    const digits = value.replace(/\D/g, '');
    if (digits.length <= 1) return '+7 (';
    if (digits.length <= 4) return `+7 (${digits.substring(1, 4)}`;
    if (digits.length <= 7) return `+7 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}`;
    if (digits.length <= 9) return `+7 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 9)}`;
    return `+7 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 9)}-${digits.substring(9, 11)}`;
  };

  const getPhoneNumber = (value: string): string => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    return digits.length > 11 ? digits.substring(0, 11) : digits;
  };

  // Обработчик события при вводе в поле ввода
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsValidPhone(false);
    setIsValidName(false);
    const formattedPhone = formatPhoneNumber(getPhoneNumber(event.target.value));
    setPhone(formattedPhone);
  };

  const submitSendCallback = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!inputName.current || !inputPhone.current) return;

    const name = inputName.current.value;

    if (getPhoneNumber(phone).length < 11) {
      setIsValidPhone(true);
    }

    if (name.length === 0) {
      setIsValidName(true);
    }

    if (getPhoneNumber(phone).length < 11 || name.length === 0) {
      toast.error('Пожалуйста, заполните все обязательные поля');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const yclid = urlParams.get('yclid');

    const jsonData = {
      site_callback_type: { tag: callback },
      user_name: name || null,
      user_phone: phone.length > 0 ? phone : null,
      yclid,
    };

    setIsSubmitting(true);

    try {
      await ApiSite.callback(jsonData);

      if (typeof window !== 'undefined' && window.ym && !isNaN(yandexMetrikaId)) {
        window.ym(yandexMetrikaId, 'reachGoal', callback);
      }

      toast.success('Ваш запрос успешно отправлен!', {
        duration: 5000,
        position: 'top-center',
      });

      setPhone('');
      inputName.current.value = '';
      setIsSuccessOpen(true);
    } catch (error) {
      toast.error('Произошла ошибка при отправке запроса. Пожалуйста, попробуйте позже.', {
        duration: 5000,
        position: 'top-center',
      });
      console.error('Callback request error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container id="callback">
      <TitleSection
        label={"Оставьте заявку на консультацию"}
        subtitle={
          "Оставьте заявку прямо сейчас и получите доступ к самым актуальным знаниям в сфере госзакупок. " +
          "Станьте профессионалом своего дела вместе с нами!"
        }
      />
      <div className={styles.cont}>
        <div>
          <div className={styles.img}>
            <img src={'/images/call_2.png'} alt={''} width={380} height={380} loading={'lazy'} />
            {/*<Image src={'/images/call_2.png'} alt={''} width={420} height={420} loading={'lazy'} />*/}
          </div>
        </div>
        <form className={styles.form} onSubmit={submitSendCallback}>
          {/*<div>*/}
          {/*  <h2>Оставьте заявку на консультацию</h2>*/}
          {/*  <p>*/}
          {/*    Оставьте заявку прямо сейчас и получите доступ к самым актуальным знаниям в сфере госзакупок.*/}
          {/*    Станьте профессионалом своего дела вместе с нами!*/}
          {/*  </p>*/}
          {/*</div>*/}

          <div className={styles.dataChunk}>
            <label>Ваше имя</label>
            <input
              placeholder={"Имя"}
              ref={inputName}
            />
            {isValidName && (
              <div className={styles.error}>Как мы можем к вам обращаться?</div>
            )}
          </div>
          <div className={styles.dataChunk}>
            <label>Ваш номер</label>
            <input
              type="text"
              value={phone}
              onChange={handleInputChange}
              onClick={() => handleInputChange({target: {value: phone}} as ChangeEvent<HTMLInputElement>)}
              ref={inputPhone}
              placeholder="+7 (___) ___-__-__"
            />
            {isValidPhone && (
              <div className={styles.error}>Пожалуйста, введите корректный номер телефона</div>
            )}
          </div>
          <div style={{width: "100%", display: "flex", flexDirection: "column", justifyContent: "center"}}>
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Отправка...' : 'Оставить заявку'}
            </button>
            <div className={styles.privacy}>
              <p>Нажимая на кнопку «Оставить заявку» вы соглашаетесь c{' '}
                <a href={'/privacy'} target={'_blank'} rel="noopener noreferrer">
                  политикой конфиденциальности
                </a>
              </p>
            </div>
          </div>
        </form>

        {/*
          Старый поток через SendCallbackModal с reCAPTCHA временно отключен.
          Заявка отправляется напрямую в submitSendCallback, после успешного ответа открывается только окно успеха.
        */}
        {isSuccessOpen && (
          <div
            className={modalStyles.overlay}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setIsSuccessOpen(false);
              }
            }}
          >
            <div className={modalStyles.modalWindow}>
              <button
                className={modalStyles.closeButton}
                onClick={() => setIsSuccessOpen(false)}
              >
                <X size={24} />
              </button>

              <div className={modalStyles.modalHeader}>
                <div className={modalStyles.successIcon}>
                  <CheckCircle size={64} />
                </div>
                <h2 className={modalStyles.modalTitle}>Запрос отправлен!</h2>
                <p className={modalStyles.modalSubtitle}>
                  Мы свяжемся с вами в ближайшее время.
                  <br />
                  Пожалуйста, оставайтесь на связи.
                </p>
              </div>

              <button
                className={modalStyles.successButton}
                onClick={() => setIsSuccessOpen(false)}
                autoFocus
              >
                Хорошо
              </button>

              <div className={modalStyles.safeArea}></div>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};
